<template>
    <div ref="target">
        <div v-if="route" class="print-button-dropdown" :class="{ 'is-open': isOpened }">
            <button
                type="button"
                class="print-button button is-outlined"
                :aria-expanded="isOpened.toString()"
                @click="isOpened = !isOpened"
            >
                <IconPrint width="16" height="16" />
                {{ t('print_schedule') }}
                <IconDropdownArrow class="icon-chevron" width="10" height="6" />
            </button>
            <div v-show="isOpened" class="print-button-dropdown-content">
                <BaseLink v-if="route.urlTracePDF" :href="route.urlTracePDF" external :icon="false">
                    {{ t('print_route') }} {{ route.noParcours }}
                </BaseLink>
                <a :href="printUrl">
                    {{ t('print_custom_schedule') }}
                </a>
            </div>
        </div>
        <a v-else class="print-button" :href="printUrl">
            <IconPrint width="16" height="16" />
            {{ t('print_custom_schedule') }}
        </a>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { onClickOutside, useFocusWithin } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import BaseLink from '@/shared/BaseLink';
import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';
import IconPrint from '@@/icons/print.svg';

defineProps({
    route: {
        type: Object,
        default: null,
    },
});

const { t, locale } = useI18n({
    messages: {
        fr: {
            print_schedule: 'Imprimer l’horaire ou le tracé',
            print_route: 'Imprimer le parcours',
            print_custom_schedule: 'Imprimer un horaire personnalisé',
        },
        en: {
            print_schedule: 'Print schedule or route',
            print_route: 'Print route',
            print_custom_schedule: 'Print a custom schedule',
        },
    },
});

const vRoute = useRoute();

const printUrl = computed(() => {
    let url = '';
    if (locale.value === 'fr') {
        url = '/horaires-et-trajets/horaires-des-bus/horaires-imprimer';
    } else {
        url = `/en/schedules-and-routes/bus-schedule/printable-schedules`;
    }
    if (vRoute.params.route && vRoute.params.direction) {
        url += `/${vRoute.params.route}/${vRoute.params.direction}`;
    }
    return url;
});

const isOpened = ref(false);

function close() {
    isOpened.value = false;
}

const target = ref();
onClickOutside(target, () => close());

const { focused } = useFocusWithin(target);
watch(focused, (isFocused) => {
    if (!isFocused) {
        close();
    }
});
</script>

<style lang="scss" scoped>
.print-button-dropdown {
    display: inline-flex;
    z-index: 1;
    position: relative;
    flex-direction: column;
    margin-left: auto;
}
.print-button {
    z-index: 1;
    text-align: left;
    text-decoration: none;
    background: $color-white;
    align-items: center;
    @include breakpoint(medium) {
        gap: 16px;
    }
    svg {
        transition: all 300ms ease;
        flex-shrink: 0;
    }
}
.icon-chevron {
    transition: transform 300ms ease;
    .is-open & {
        transition: transform 300ms ease;
        transform: rotate(180deg);
    }
}
.print-button-dropdown-content {
    position: absolute;
    top: 100%;
    right: 0;
    left: 0;
    overflow: hidden;
    border: 1px solid $color-blue-60;
    display: flex;
    flex-direction: column;
    border-radius: 15px;
    background: $color-white;
    margin-top: 16px;
    @include breakpoint(medium) {
        border-radius: 20px;
    }
    .is-open & {
        box-shadow: $dropdown-shadow;
    }
    a {
        @include accessible-focus;
        @include rem-typo(14px);
        margin-top: -1px;
        padding: 12px 16px;
        text-decoration: none;
        border-top: 1px solid $color-blue-60;
        @include breakpoint(medium) {
            @include rem-typo(16px);
        }
        &:first-child {
            border-top: 0;
        }
    }
    button,
    a {
        @include rem-typo(16px);
        transition: all 300ms ease;
        &:hover {
            color: $color-white;
            background-color: $color-blue-60;
            transition: all 300ms ease;
            svg {
                fill: $color-white;
                transition: all 300ms ease;
            }
        }
    }
}
</style>
